import { supabase } from '../lib/supabase';

/**
 * Credit Service for CareerLift
 * Handles credit pack purchases, balance tracking, and usage management
 */

class CreditService {
  /**
   * Get available credit packs
   */
  static async getCreditPacks() {
    try {
      const { data, error } = await supabase
        .from('credit_packs')
        .select('*')
        .eq('is_active', true)
        .order('credits', { ascending: true });

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error fetching credit packs:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user's current credit balance
   */
  static async getUserCreditBalance(userId) {
    try {
      // For now, return a mock balance since the database function doesn't exist yet
      console.log('Getting credit balance for user:', userId);
      return { success: true, balance: 0 };
    } catch (error) {
      console.error('Error fetching user credit balance:', error);
      return { success: false, error: error.message, balance: 0 };
    }
  }

  /**
   * Get user's credit history
   */
  static async getUserCreditHistory(userId, limit = 50) {
    try {
      const { data, error } = await supabase
        .from('credit_transactions')
        .select(`
          *,
          credit_pack:credit_packs(name, credits)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error fetching credit history:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Add credits to user account (for purchases)
   */
  static async addCredits(userId, credits, transactionType = 'purchase', description = null, metadata = {}) {
    try {
      // For now, just log the action since the database function doesn't exist yet
      console.log('Adding credits:', { userId, credits, transactionType, description, metadata });
      return { success: true, data: { id: 'mock-transaction-id' } };
    } catch (error) {
      console.error('Error adding credits:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Spend credits from user account
   */
  static async spendCredits(userId, credits, description = null, metadata = {}) {
    try {
      const { data, error } = await supabase
        .rpc('spend_user_credits', {
          p_user_id: userId,
          p_credits: credits,
          p_description: description,
          p_metadata: metadata
        });

      if (error) throw error;

      return { success: data, error: data ? null : 'Insufficient credits' };
    } catch (error) {
      console.error('Error spending credits:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Check if user can take an assessment (has credits or is premium)
   */
  static async canTakeAssessment(userId, isPremium = false) {
    try {
      // Premium users can take assessments (they have access to credit system)
      if (isPremium) {
        return { success: true, canTake: true, reason: 'premium' };
      }

      // Check credit balance for free users
      const balanceResult = await this.getUserCreditBalance(userId);
      if (!balanceResult.success) {
        return { success: false, error: balanceResult.error };
      }

      const hasCredits = balanceResult.balance > 0;
      return { 
        success: true, 
        canTake: hasCredits, 
        reason: hasCredits ? 'credits' : 'no_credits',
        creditBalance: balanceResult.balance
      };
    } catch (error) {
      console.error('Error checking assessment eligibility:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Record credit usage for an assessment
   */
  static async recordAssessmentCreditUsage(userId, assessmentId, creditsUsed = 1) {
    try {
      const { data, error } = await supabase
        .from('user_assessment_credits')
        .insert({
          user_id: userId,
          assessment_id: assessmentId,
          credits_used: creditsUsed
        })
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error recording assessment credit usage:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get credit pack by lookup key
   */
  static async getCreditPackByLookupKey(lookupKey) {
    try {
      const { data, error } = await supabase
        .from('credit_packs')
        .select('*')
        .eq('lookup_key', lookupKey)
        .eq('is_active', true)
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error fetching credit pack by lookup key:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update credit pack with Stripe data
   */
  static async updateCreditPackStripeData(lookupKey, stripePriceId, stripeProductId) {
    try {
      const { data, error } = await supabase
        .from('credit_packs')
        .update({
          stripe_price_id: stripePriceId,
          stripe_product_id: stripeProductId
        })
        .eq('lookup_key', lookupKey)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error updating credit pack Stripe data:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Calculate credit pack value proposition
   */
  static calculateCreditPackValue(credits, priceCents) {
    const pricePerCredit = priceCents / credits;
    const monthlyPremiumCost = 1999; // $19.99 in cents
    const premiumCreditsPerMonth = 3; // Premium includes 3 credits per month
    const premiumPricePerCredit = monthlyPremiumCost / premiumCreditsPerMonth;
    
    return {
      pricePerCredit,
      premiumPricePerCredit,
      savingsVsPremium: Math.max(0, premiumPricePerCredit - pricePerCredit),
      isBetterThanPremium: pricePerCredit < premiumPricePerCredit,
      valueScore: Math.round((premiumPricePerCredit / pricePerCredit) * 100) / 100
    };
  }

  /**
   * Get recommended credit pack based on usage
   */
  static getRecommendedCreditPack(creditPacks, monthlyUsage = 0) {
    if (monthlyUsage <= 1) {
      return creditPacks.find(pack => pack.credits === 1);
    } else if (monthlyUsage <= 3) {
      return creditPacks.find(pack => pack.credits === 3);
    } else if (monthlyUsage <= 5) {
      return creditPacks.find(pack => pack.credits === 5);
    } else {
      return creditPacks.find(pack => pack.credits === 10);
    }
  }
}

export default CreditService;

